import { isWorkEmail } from 'utils/email-domains'
import { OTHER_INDUSTRY, WEBSITE_INDUSTRIES } from 'utils/website-industry'
import {
	AppButton,
	Cascader,
	CascaderValue,
	Collapse,
	Input
} from '@clickguard/design-system'
import { useState, useMemo, useEffect } from 'react'
import { GTooltip } from 'components/basic-blocks'
import { GInput } from 'components/basic-blocks/g-input/g-input'
import { AiOutlineQuestionCircle } from 'react-icons/ai'
import { useFeatureFlag } from 'services/feature-flags/hooks'
import { ProductAccountDTO, UserDTO } from 'api-models'
import { Onboarding } from 'services/user'
import { useStores } from 'hooks'
import { isValidDomain, setItemsToLocalStorage } from 'utils'
import { parseError } from 'utils/errors'
import { trackWebsiteAdded } from 'utils/ga4-events'

const extractDomainFromEmail = (email: string): string => {
	if (!email) return ''
	const domain = email.split('@')[1]
	return domain || ''
}

interface ProtectWebsiteComponentProps {
	account: ProductAccountDTO
	user: UserDTO
	onSuccess: (assetId: string) => void
}

export const ProtectWebsiteComponent = ({
	account,
	user,
	onSuccess
}: ProtectWebsiteComponentProps) => {
	const { assetStore } = useStores()
	const { setAsset, getAllAssets, allAssets } = assetStore

	const hasAssets = allAssets.some((a) => a.account_id === account.id)

	// If onboarding already captured a domain (Block 1), use it directly for the first website
	const onboardingDomain = account.setup?.domain || ''

	// Calculate initial website URL from user email if it's a work email
	const initialWebsiteUrl = useMemo(() => {
		if (hasAssets) {
			return ''
		}
		if (onboardingDomain) {
			return onboardingDomain
		}
		if (user.email && !(user.is_personal_email ?? !isWorkEmail(user.email))) {
			return extractDomainFromEmail(user.email)
		}
		return ''
	}, [user.email, user.is_personal_email, onboardingDomain, hasAssets])

	const isAgency = account.isAgency === true

	const workspaceName = account.name
	const [websiteUrl, setWebsiteUrl] = useState(
		isAgency && !hasAssets
			? onboardingDomain
				? onboardingDomain
				: ''
			: initialWebsiteUrl
	)
	useEffect(() => {
		if (onboardingDomain && !websiteUrl && !hasAssets) {
			setWebsiteUrl(
				isAgency
					? onboardingDomain
						? onboardingDomain
						: ''
					: initialWebsiteUrl
			)
		}
	}, [onboardingDomain, isAgency, initialWebsiteUrl, hasAssets])
	const [aiMode, setAiMode] = useState<'monitoring' | 'protection'>(
		'protection'
	)
	const [industry, setIndustry] = useState<CascaderValue | null>(null)
	const [industryOther, setIndustryOther] = useState('')
	const [industryOpen, setIndustryOpen] = useState(false)
	const isOtherIndustry = industry?.categoryId === OTHER_INDUSTRY
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const industryMissing =
		!industry || (isOtherIndustry && !industryOther.trim())

	const showMonitoringMode = useFeatureFlag(
		'setup-flow-v2-website-monitoring-mode-shown'
	)

	const handleContinue = async () => {
		if (!account?.id) {
			setError('Account is required')
			return
		}

		// For first website: use onboarding domain if available, otherwise require input
		const domain =
			!hasAssets && onboardingDomain ? onboardingDomain : websiteUrl.trim()

		if (!domain) {
			setError('Website URL is required')
			return
		}

		// Same rule as the website step before the plan page.
		if (!isValidDomain(domain)) {
			setError('Please enter a valid domain (e.g. example.com)')
			return
		}

		if (industryMissing) {
			setError('Pick the industry of this website')
			return
		}

		setLoading(true)
		setError(null)

		try {
			// Create asset with the website URL and configuration
			// If feature flag is off, configuration will be null and defaults will be used
			const createdAsset = await Onboarding.createAsset(account.id, {
				domain,
				sector: industry?.categoryId ?? null,
				industry: isOtherIndustry ? null : industry?.itemId ?? null,
				industry_other: isOtherIndustry ? industryOther.trim() : null,
				protection_enabled: showMonitoringMode
					? aiMode === 'protection'
					: undefined
			})

			// Set the created asset in local storage and asset store
			if (createdAsset) {
				setItemsToLocalStorage([
					{ key: 'selected_asset', value: createdAsset.id },
					{ key: 'selected_account', value: account.id }
				])
				setAsset(createdAsset)
				// Refresh assets list to include the newly created asset
				await getAllAssets()
			}

			trackWebsiteAdded({
				workspace_id: account.id,
				website: websiteUrl
			})

			// Call onDone callback to notify parent component
			onSuccess(createdAsset.id)
		} catch (err) {
			setError(parseError(err) || 'Failed to save settings')
		} finally {
			setLoading(false)
		}
	}

	return (
		<div
			className="bg-white rounded-[24px] shadow-lg md:px-12 md:py-8 p-6"
			style={{ width: '620px', maxWidth: '100%' }}
		>
			<div className="flex flex-col md:gap-8 gap-4">
				{/* Header */}
				<div className="flex flex-col gap-4">
					<h1 className="md:text-[30px] text-[24px] md:leading-[36px] leading-[28px] font-medium text-t-default">
						{hasAssets ? 'Protect a new website' : 'Set up detection'}
					</h1>
					{hasAssets && (
						<p className="text-[16px] leading-[22px] text-t-checkbox-unactive">
							Add a new website to{' '}
							<span className="font-semibold text-t-email-blue">
								{workspaceName}
							</span>
							.
						</p>
					)}
					{!hasAssets && onboardingDomain && (
						/* First website with domain from onboarding — show inline in description */
						<p className="text-[16px] leading-[22px] text-t-checkbox-unactive">
							Tailor protection setup for{' '}
							<span className="font-semibold text-t-email-blue">
								{onboardingDomain}
							</span>
						</p>
					)}
				</div>

				{/* Website URL */}
				{!hasAssets && onboardingDomain ? (
					<></>
				) : (
					/* Adding another website OR no onboarding domain — show editable input */
					<div className="flex flex-col gap-[6px]">
						<div className="flex items-center gap-2">
							<label className="text-[14px] leading-[18px] font-medium text-t-google-ad">
								{hasAssets
									? 'Website to protect (URL)'
									: 'What is the first website you wish to protect?'}
							</label>
							{hasAssets && (
								<GTooltip
									content="Website or landing page you want to protect from click fraud."
									position="top"
									arrowClassName="w-0 h-0 absolute bottom-[0.5px] border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-tooltip-caret"
									contentClassName="border-none mb-2 bg-t-smoke font-medium text-sm text-t-desc-gray"
								>
									<AiOutlineQuestionCircle className="text-t-gray-hold w-4 h-4" />
								</GTooltip>
							)}
						</div>
						<GInput
							value={websiteUrl}
							onChange={(e) => {
								setWebsiteUrl(e.target.value)
								setError(null)
							}}
							placeholder={
								hasAssets
									? isAgency
										? 'Website you want to protect (e.g. clientwebsite.com)'
										: 'Your domain (e.g. clientwebsite.com)'
									: 'www.example.com'
							}
							className="w-full"
							inputClassName={`${
								hasAssets ? 'px-[14px] py-[10px]' : 'px-3 py-2'
							} border border-t-gray-input rounded-[8px] focus:border-[#554BFD] focus:outline-none shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] text-[16px] leading-[22px] text-t-coupon placeholder:text-t-gray-hold`}
						/>
					</div>
				)}

				{/* Industry of the website being protected (not the agency's own) */}
				<div className="flex flex-col gap-[6px]">
					<div className="flex items-center gap-2">
						<label className="text-[14px] leading-[18px] font-medium text-t-google-ad">
							What industry is this website in?
						</label>
						<GTooltip
							content={
								isAgency
									? 'The industry of the website, not of your agency. It helps ClickGuard fit its detection to that industry.'
									: 'It helps ClickGuard fit its detection to your industry.'
							}
							position="top"
							arrowClassName="w-0 h-0 absolute bottom-[0.5px] border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-tooltip-caret"
							contentClassName="border-none mb-2 bg-t-smoke font-medium text-sm text-t-desc-gray"
						>
							<AiOutlineQuestionCircle className="text-t-gray-hold w-4 h-4" />
						</GTooltip>
					</div>
					<div>
						<Cascader
							fullWidth
							size="lg"
							placeholder="Pick an industry"
							categories={WEBSITE_INDUSTRIES}
							value={industry}
							open={industryOpen}
							onOpenChange={setIndustryOpen}
							onChange={(value) => {
								setIndustry(value)
								setError(null)
								// "Other" has nothing to narrow to: close and let them type.
								if (value.categoryId === OTHER_INDUSTRY) setIndustryOpen(false)
							}}
						/>
						{/* Collapse clips its overflow while it animates; the 2px of room
						    on each side keeps the Input's 2px focus ring whole. */}
						<Collapse open={isOtherIndustry} className="-mx-[2px]">
							<div className="pt-2 px-[2px] pb-[2px]">
								<Input
									size="md"
									state="default"
									autoFocus
									aria-label="Your industry"
									placeholder="Type your industry"
									value={industryOther}
									onChange={(e) => {
										setIndustryOther(e.target.value)
										setError(null)
									}}
								/>
							</div>
						</Collapse>
					</div>
				</div>

				{/* Mode Selection - Only shown if feature flag is enabled */}
				{showMonitoringMode && (
					<div className="flex flex-col gap-3">
						<p className="text-[14px] leading-[18px] font-medium text-t-google-ad">
							Choose a starting mode
						</p>
						<div className="flex flex-col gap-4">
							{/* Full protection */}
							<label
								className="flex items-center gap-3 cursor-pointer"
								onClick={() => setAiMode('protection')}
							>
								<input
									type="radio"
									name="aiMode"
									value="protection"
									checked={aiMode === 'protection'}
									onChange={() => setAiMode('protection')}
									className="sr-only"
								/>
								<div
									className={`w-4 h-4 rounded-[8px] border flex items-center justify-center flex-shrink-0 ${
										aiMode === 'protection'
											? 'bg-[#554BFD] border-[#554BFD]'
											: 'border-t-gray-input bg-white'
									}`}
								>
									{aiMode === 'protection' && (
										<div className="w-[6px] h-[6px] rounded-full bg-white" />
									)}
								</div>
								<div className="flex-1 flex flex-col">
									<div className="flex items-center gap-[6px]">
										<p className="text-[16px] leading-[22px] font-medium text-t-default">
											Block attackers and unwanted traffic
										</p>
										<span className="rounded-full border border-brand bg-t-step-inactive px-[6px] text-[10px] font-semibold leading-[16px] text-[color:var(--brand-chip-ink,#3420a4)]">
											Recommended
										</span>
									</div>
									<p className="text-[14px] leading-[18px] text-t-desc-gray">
										Monitors and blocks bad traffic sources automatically.
									</p>
								</div>
							</label>
							{/* Monitoring only */}
							<label
								className="flex items-center gap-3 cursor-pointer"
								onClick={() => setAiMode('monitoring')}
							>
								<input
									type="radio"
									name="aiMode"
									value="monitoring"
									checked={aiMode === 'monitoring'}
									onChange={() => setAiMode('monitoring')}
									className="sr-only"
								/>
								<div
									className={`w-4 h-4 rounded-[8px] border flex items-center justify-center flex-shrink-0 ${
										aiMode === 'monitoring'
											? 'bg-[#554BFD] border-[#554BFD]'
											: 'border-t-gray-input bg-white'
									}`}
								>
									{aiMode === 'monitoring' && (
										<div className="w-[6px] h-[6px] rounded-full bg-white" />
									)}
								</div>
								<div className="flex-1 flex flex-col">
									<p className="text-[16px] leading-[22px] font-medium text-t-default">
										Monitoring only
									</p>
									<p className="text-[14px] leading-[18px] text-t-desc-gray">
										Allows you to monitor your traffic from our dashboards,
										without blocking any bad source.
									</p>
								</div>
							</label>
						</div>
					</div>
				)}

				{/* Error message */}
				{error && (
					<div className="p-3 bg-red-50 border border-red-200 rounded-lg">
						<p className="text-red-600 text-sm">{error}</p>
					</div>
				)}

				{/* Continue button */}
				<AppButton
					variant="marketing"
					size="lg"
					fullWidth
					label="Continue"
					isLoading={loading}
					loader="dots"
					disabled={loading || !websiteUrl.trim() || industryMissing}
					onClick={handleContinue}
				/>
			</div>
		</div>
	)
}
